import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Col, Dropdown, Menu, Row, Typography } from "antd";
import { Fragment } from "react/jsx-runtime";
import { journalsQueries } from "@/entities/journal";
import {
  generateAddressTypeName,
  ProjectParticipantInfo,
} from "@/entities/project";
import { useValidateTitle } from "@/features/journal-title-validation";
import { ParticipantsInfiniteScroll } from "@/features/participants-infinite-scroll";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator } from "@/shared/lib/hooks";
import { fallbackString, formatDate } from "@/shared/utils";
import { useGetRepresentative } from "../../api";
import {
  useCreateParticipant,
  useDeleteParticipant,
  useTitleAmendmentMode,
} from "../../model";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";
import {
  BuildingContractorControls,
  BuildingContractorRepresentative,
} from "./ui";

export const BuildingContractorCompanyTableNew = () => {
  const { titleId } = useCoordinator();
  const { projectId = "" } = useCoordinator();
  const { data: titleJournal } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const { isLoading } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const titleStatus =
    titleJournal?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !titleJournal?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const { errorList } = useValidateTitle();

  const {
    representative: buildingContractor,
    isFetching: isBuildingContractorFetching,
  } = useGetRepresentative("buildingContractor");

  const validationErrors = useMemo(() => {
    if (!buildingContractor?.company) {
      return [];
    }
    return errorList.filter((error) => [10, 25].includes(error.id));
  }, [errorList, buildingContractor]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const { mutate: createParticipant, isPending: createParticipantPending } =
    useCreateParticipant({ titleId });

  const { mutate: deleteParticipant, isPending: deleteParticipantPending } =
    useDeleteParticipant({ titleId });

  const onPartySelect = async (participant: ProjectParticipantInfo) => {
    if (participant) {
      createParticipant({
        participantType: "buildingContractor",
        projectParticipantId: participant.projectParticipantId,
        companyId: participant.company.companyId,
      });
    }
  };

  const handleDelete = () => {
    deleteParticipant({
      projectParticipantId: buildingContractor?.projectParticipantId ?? "",
      participantType: "buildingContractor",
    });
  };

  const menu = (
    <Menu
      items={[
        {
          key: "delete",
          label: "Удалить",
          icon: <BasketIcon />,
          onClick: handleDelete,
        },
      ]}
    />
  );

  const company = buildingContractor?.company;
  const sro = company?.sro;

  return (
    <CollapsibleSection
      title="Лицо, осуществляющее строительство"
      spinning={
        isLoading || deleteParticipantPending || isBuildingContractorFetching
      }
      validationErrors={validationErrors}
      sectionId="building-contractor-company"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty={!buildingContractor?.company}
    >
      <>
        {company ? (
          <div>
            <div className="journal-title-block-header">
              <div className="journal-title-company-name">
                <div>
                  {fallbackString(company.fullName || company.shortName)}
                </div>
              </div>
              {titleStatus && !isNewAmendment ? (
                <Can I="edit" a="title_journals">
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <span className="journal-title-menu-trigger">
                      <MoreOneIcon width={24} height={24} />
                    </span>
                  </Dropdown>
                </Can>
              ) : null}
            </div>
            <Row gutter={[16, 16]} className="journal-title-info-grid">
              <Col span={8} style={{ color: "#888" }}>
                ИНН
              </Col>
              <Col span={16} className="table-info-content">
                {fallbackString(company?.inn)}
              </Col>
              <Col span={8} style={{ color: "#888" }}>
                КПП
              </Col>
              <Col span={16} className="table-info-content">
                {fallbackString(company?.kpp)}
              </Col>
              <Col span={8} style={{ color: "#888" }}>
                ОГРН
              </Col>
              <Col span={16} className="table-info-content">
                {fallbackString(company?.ogrn)}
              </Col>
              <Col span={8} style={{ color: "#888" }}>
                Контакты
              </Col>
              <Col span={16} className="table-info-content">
                {fallbackString(
                  (company?.contacts?.phone ? company.contacts.phone : "") +
                    (company?.contacts?.email ? +company.contacts.email : ""),
                )}
              </Col>
              <Col span={8} style={{ color: "#888" }}>
                Адрес
              </Col>
              <Col span={16} className="table-info-content">
                {fallbackString(
                  generateAddressTypeName(
                    // @ts-expect-error fullAddress отсутствует в openapi типе адреса компании
                    company?.addresses?.legal?.fullAddress,
                  ),
                )}
              </Col>
            </Row>
            {sro?.length ? (
              sro.map((sroItem) => {
                return (
                  <Fragment key={sroItem.sroId}>
                    <Typography.Title
                      level={5}
                      style={{
                        fontWeight: "400",
                        marginTop: "16px",
                        marginBottom: 12,
                      }}
                    >
                      СРО{" "}
                      {sroItem.sroType === "ENGINEERING"
                        ? "Проектирование"
                        : "Строительство"}
                    </Typography.Title>
                    <Row gutter={[16, 16]}>
                      <Fragment>
                        <Col span={8} style={{ color: "#888" }}>
                          Номер
                        </Col>
                        <Col span={16}>
                          {fallbackString(sroItem?.sroNumber)}
                        </Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888" }}>
                          Дата
                        </Col>
                        <Col span={16}>
                          {fallbackString(formatDate(sroItem?.sroDate))}
                        </Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888" }}>
                          Выдано
                        </Col>
                        <Col span={16}>
                          {fallbackString(sroItem?.sroIsserName)}
                        </Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888" }}>
                          ИНН
                        </Col>
                        <Col span={16}>
                          {fallbackString(sroItem?.sroIsserInn)}
                        </Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888" }}>
                          ОГРН
                        </Col>
                        <Col span={16}>
                          {fallbackString(sroItem?.sroIsserOgrn)}
                        </Col>
                      </Fragment>
                    </Row>
                  </Fragment>
                );
              })
            ) : (
              <Fragment>
                <Typography.Title
                  level={5}
                  style={{
                    fontWeight: "400",
                    marginTop: "16px",
                    marginBottom: 12,
                  }}
                >
                  СРО Строительство
                </Typography.Title>
                <Row gutter={[16, 16]}>
                  <Fragment>
                    <Col span={8} style={{ color: "#888" }}>
                      Номер
                    </Col>
                    <Col span={16}>{fallbackString()}</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888" }}>
                      Дата
                    </Col>
                    <Col span={16}>{fallbackString()}</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888" }}>
                      Выдано
                    </Col>
                    <Col span={16}>{fallbackString()}</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888" }}>
                      ИНН
                    </Col>
                    <Col span={16}>{fallbackString()}</Col>
                  </Fragment>
                  <Fragment>
                    <Col span={8} style={{ color: "#888" }}>
                      ОГРН
                    </Col>
                    <Col span={16}>{fallbackString()}</Col>
                  </Fragment>
                </Row>
              </Fragment>
            )}
          </div>
        ) : (
          <>
            {canAddData ? (
              <Can I="edit" a="title_journals">
                <div>
                  <div className="journal-title-org-heading">Организация</div>
                  <ParticipantsInfiniteScroll
                    projectId={projectId}
                    onPartySelect={onPartySelect}
                    isLoading={createParticipantPending}
                  />
                </div>
              </Can>
            ) : null}
          </>
        )}
      </>
      <BuildingContractorRepresentative />
      <BuildingContractorControls />
    </CollapsibleSection>
  );
};
